import {Fragment, useContext} from 'react';
import {useMutation, useQueryClient} from '@tanstack/react-query';
import {AxiosError} from 'axios';
import {CareJourneyAttachmentType, CareJourneyDetailsResponse, CareJourneyFaxStatus} from '@/api/dto/care-journey';
import {CarecoApiContext} from '@/app-context/careco-api-context';
import {useActiveRoster} from '@/store/roster_store';
import {useAlertStore} from '@/store/alert_store';
import {ToastType} from 'patient-ping-remedy/packages/toast';
import {GENERICERROR} from '@manual_encounters/constants/BannerMessages';
import Helpers from '@/helpers/helpers';
import DisplayHelpers from '@/helpers/display_helpers';
import {Section} from '@/components/pages/tasks/task_edit/progress/TaskEditDetails.styles';
import {
  CareNoteDirectAddress,
  CareNotStatus,
  SectionContent,
  SectionHeaderRow,
  SectionTitleRow,
  StandardSmallTypography
} from '@/components/pages/tasks/task_edit/progress/CareNoteSection.styles';
import {StandardSemiBoldTypography} from 'patient-ping-remedy/packages/typography';
import Button, {StyleType} from 'patient-ping-remedy/packages/button';
import Icon, {ICONS} from 'patient-ping-remedy/packages/icon';
import Spacer from 'patient-ping-remedy/packages/spacer';
import {colors, sizes} from 'patient-ping-remedy/packages/theme';
import CustomExpandMoreWidget from '@common/CustomExpandMoreButton';
import {
  ReferralFaxRow,
  ReferralFaxDetails
} from '@/components/pages/tasks/task_edit/progress/CareTeamReferralSection.styles';

type Props = {
  careJourneyDetails: CareJourneyDetailsResponse;
}

const STATUS_MAPPING = {
  [CareJourneyFaxStatus.WAITING]: {
    title: 'Sending in progress',
    iconClass: ICONS['spinner'],
    iconColor: colors.green4,
    dateSuffix: 'Initiated on',
  },
  [CareJourneyFaxStatus.IN_PROGRESS]: {
    title: 'Sending in progress',
    iconClass: ICONS['spinner'],
    iconColor: colors.green4,
    dateSuffix: 'Initiated on',
  },
  [CareJourneyFaxStatus.COMPLETE]: {
    title: 'Sent successfully',
    iconClass: ICONS['check-circle'],
    iconColor: colors.green5,
    dateSuffix: 'Sent on',
  },
  [CareJourneyFaxStatus.FAILED]: {
    title: 'Failed to send',
    iconClass: ICONS['exclamation-triangle'],
    iconColor: colors.yellow5,
    dateSuffix: 'Initiated on',
  },
};

export default function CareTeamReferralSection({careJourneyDetails}: Props) {
  const {carecoApi} = useContext(CarecoApiContext);
  const currentRoster = useActiveRoster();
  const {addAlert} = useAlertStore();
  const queryClient = useQueryClient();

  const referralAttachments = careJourneyDetails.attachments?.filter(
    attachment => attachment.type === CareJourneyAttachmentType.CARE_TEAM_REFERRAL
  ) ?? [];

  // Only IN_PROGRESS counts as in flight. A referral is submitted to the fax provider in the same
  // request that creates it, so WAITING only persists when faxing is disabled or submission threw,
  // and nothing advances it afterwards. Treating WAITING as in flight would disable the button
  // permanently for that journey.
  const hasInProgressReferral = referralAttachments.some(
    attachment => attachment.fax?.externalStatus === CareJourneyFaxStatus.IN_PROGRESS
  );

  const {mutate: sendReferral, isPending} = useMutation({
    mutationFn: async () =>
      carecoApi?.sendCareTeamReferral(
        careJourneyDetails.careJourneyInstanceId,
        currentRoster!.id
      ),
    onSuccess(attachment) {
      addAlert({
        type: ToastType.SUCCESS,
        content: 'Care team referral sent successfully.',
      });
      queryClient.setQueryData(
        ['careJourneyDetails', careJourneyDetails.careJourneyInstanceId],
        (old?: CareJourneyDetailsResponse) => {
          if (!old || !attachment) return old;
          return {
            ...old,
            attachments: [...(old.attachments ?? []), attachment],
          };
        }
      );
      void queryClient.invalidateQueries({
        queryKey: ['careJourneyDetails', careJourneyDetails.careJourneyInstanceId],
      });
    },
    onError(error: AxiosError) {
      Helpers.alertOnError(
        `Failed to send care team referral. ${GENERICERROR} ${Helpers.traceId(error)}`, error);
    },
  });

  if (!currentRoster?.configuration?.manualCareTeamReferralEnabled) {
    return null;
  }

  return (
    <Section>
      <SectionContent>
        <SectionHeaderRow>
          <SectionTitleRow>
            <StandardSemiBoldTypography>Care Team Referral</StandardSemiBoldTypography>
          </SectionTitleRow>
          <SectionTitleRow>
            <Button
              onClick={() => sendReferral()}
              styleType={StyleType.SECONDARY}
              disabled={isPending || hasInProgressReferral}
            >
              Send Care Team Referral
            </Button>
          </SectionTitleRow>
        </SectionHeaderRow>

        {referralAttachments.map((attachment) => {
          const externalStatus = attachment.fax?.externalStatus ?? CareJourneyFaxStatus.IN_PROGRESS;
          const mapping = STATUS_MAPPING[externalStatus];

          return (
            <ReferralFaxRow key={attachment.id}>
              <CustomExpandMoreWidget
                arrowPosition={'right'}
                title={
                  <Fragment>
                    <Icon
                      iconClass={mapping.iconClass}
                      color={mapping.iconColor}
                      weight="far"
                      iconSize="1x"
                    />
                    <Spacer itemWidth={sizes.xSmall}/>
                    <CareNotStatus>
                      {mapping.title}
                    </CareNotStatus>
                    {attachment.fax?.fax &&
                      <CareNoteDirectAddress>
                        to {DisplayHelpers.formatPhoneNumber(attachment.fax.fax)}
                      </CareNoteDirectAddress>
                    }
                  </Fragment>
                }
              >
                <ReferralFaxDetails>
                  <StandardSmallTypography>
                    {attachment.createdAt &&
                      <Fragment>
                        <span>{mapping.dateSuffix} </span>
                        {DisplayHelpers.formatDateTimeLong(attachment.createdAt, true)}
                      </Fragment>
                    }
                  </StandardSmallTypography>
                </ReferralFaxDetails>
              </CustomExpandMoreWidget>
            </ReferralFaxRow>
          );
        })}
      </SectionContent>
    </Section>
  );
}
